import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, FileText, Search, RefreshCw, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAdminData, filterData } from '../hooks/useAdminData';
import { AdminStats } from '../components/admin/AdminStats';
import { ContactsList } from '../components/admin/ContactsList';
import { IntakesList } from '../components/admin/IntakesList';
import { IntakeDetailView } from '../components/admin/IntakeDetailView';
import { AppointmentsList } from '../components/admin/AppointmentsList';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { contacts, intakes, appointments, loading, fetchData, updateStatus } = useAdminData();
  const [tab, setTab] = useState('contacts');
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [search, setSearch] = useState('');

  const filteredContacts = filterData(contacts, ['name', 'email', 'phone', 'message'], search);
  const filteredIntakes = filterData(intakes, ['firstName', 'lastName', 'email', 'phone', 'reasonForVisit'], search);
  const filteredAppointments = filterData(appointments, ['name', 'email', 'phone', 'confirmationCode', 'dateLabel'], search);

  const handleIntakeStatusUpdate = (type, id, newStatus) => {
    updateStatus(type, id, newStatus);
    if (selectedIntake && selectedIntake.id === id) {
      setSelectedIntake({ ...selectedIntake, status: newStatus });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-warmBeige to-white py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-darkBrown hover:text-primaryOrange mr-4">
              <ArrowLeft size={20} className="mr-1" /> Home
            </Button>
            <h1 className="text-3xl font-bold text-darkBrown">Admin Dashboard</h1>
          </div>
          <Button onClick={fetchData} variant="outline" className="border-primaryOrange text-primaryOrange" data-testid="admin-refresh-btn">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        </div>

        <AdminStats contacts={contacts} intakes={intakes} appointments={appointments} />

        {/* Tabs + Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex gap-2">
            <Button
              onClick={() => { setTab('contacts'); setSelectedIntake(null); }}
              className={tab === 'contacts' ? 'bg-primaryOrange text-white' : 'bg-white text-darkBrown border'}
              data-testid="admin-tab-contacts"
            >
              <Mail size={16} className="mr-2" /> Contacts ({contacts.length})
            </Button>
            <Button
              onClick={() => { setTab('intakes'); setSelectedIntake(null); }}
              className={tab === 'intakes' ? 'bg-primaryOrange text-white' : 'bg-white text-darkBrown border'}
              data-testid="admin-tab-intakes"
            >
              <FileText size={16} className="mr-2" /> Intake Forms ({intakes.length})
            </Button>
            <Button
              onClick={() => { setTab('appointments'); setSelectedIntake(null); }}
              className={tab === 'appointments' ? 'bg-primaryOrange text-white' : 'bg-white text-darkBrown border'}
              data-testid="admin-tab-appointments"
            >
              <CalendarDays size={16} className="mr-2" /> Appointments ({appointments.length})
            </Button>
          </div>
          <div className="relative w-full md:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="admin-search"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading...</div>
        ) : (
          <>
            {tab === 'contacts' && (
              <ContactsList contacts={filteredContacts} onUpdateStatus={updateStatus} />
            )}

            {tab === 'intakes' && !selectedIntake && (
              <IntakesList
                intakes={filteredIntakes}
                onSelectIntake={setSelectedIntake}
                onUpdateStatus={updateStatus}
              />
            )}

            {tab === 'intakes' && selectedIntake && (
              <IntakeDetailView
                intake={selectedIntake}
                onBack={() => setSelectedIntake(null)}
                onUpdateStatus={handleIntakeStatusUpdate}
              />
            )}

            {tab === 'appointments' && (
              <AppointmentsList appointments={filteredAppointments} onUpdateStatus={updateStatus} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
